import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { setAuthOrgId } from '../utils/demoHelper';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '08') return true;
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [organization, setOrganization] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [isDemoMode, setIsDemoMode] = useState(() => localStorage.getItem('demo_mode') === 'true');

  const clearAuthStorage = () => {
    // Clear all Supabase-related keys from both storages
    [localStorage, sessionStorage]?.forEach(storage => {
      Object.keys(storage)?.forEach(key => {
        if (key?.startsWith('sb-')) storage?.removeItem(key);
      });
    });
  };

  const isRefreshTokenError = (error) => {
    if (!error) return false;
    const msg = (error?.message ?? '')?.toLowerCase();
    return (msg?.includes('refresh token') ||
    msg?.includes('invalid refresh token') ||
    msg?.includes('token not found') ||
    msg?.includes('refresh_token_not_found') ||
    error?.name === 'AuthApiError' ||
    error?.status === 400 || error?.code === 'refresh_token_not_found');
  };

  const handleInvalidSession = async () => {
    clearAuthStorage();
    try { await supabase?.auth?.signOut({ scope: 'local' }); } catch (_) { /* ignore */ }
    setUser(null);
    setOrganization(null);
    setSubscription(null);
  };

  useEffect(() => {
    let authSubscription = null;

    // Global safety net: catch any unhandled refresh token promise rejections
    const handleUnhandledRejection = (event) => {
      const error = event?.reason;
      if (isRefreshTokenError(error)) {
        event?.preventDefault?.();
        handleInvalidSession();
      }
    };
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    const init = async () => {
      // If demo mode is active, restore demo session without Supabase auth
      if (localStorage.getItem('demo_mode') === 'true') {
        const storedOrgId = localStorage.getItem('demo_org_id');
        if (!storedOrgId) {
          // Re-fetch demo org id using anon role (now allowed by RLS policy)
          try {
            const { data: demoOrg } = await supabase
              ?.from('organizations')
              ?.select('*')
              ?.eq('is_demo', true)
              ?.limit(1)
              ?.single();
            if (demoOrg?.id) {
              localStorage.setItem('demo_org_id', demoOrg?.id);
              setOrganization(demoOrg);
            }
          } catch (err) {
            console.warn('Could not restore demo org:', err);
          }
        }
        setUser({ email: 'demo.admin@adubuild.com', id: 'demo-user-id' });
        setLoading(false);
        return;
      }

      // Normal auth flow
      let session = null;
      try {
        const { data, error } = await supabase?.auth?.getSession();
        if (error) {
          if (isRefreshTokenError(error)) {
            await handleInvalidSession();
            setLoading(false);
            return;
          }
          setLoading(false);
          return;
        }
        session = data?.session;
      } catch (err) {
        if (isRefreshTokenError(err)) {
          await handleInvalidSession();
        }
        setLoading(false);
        return;
      }

      setUser(session?.user ?? null);
      if (session?.user) {
        await fetchUserOrganization(session?.user?.id);
      }
      setLoading(false);

      // Listen for auth changes
      const { data: { subscription } } = supabase?.auth?.onAuthStateChange(async (_event, session) => {
        if (_event === 'TOKEN_REFRESH_FAILED') {
          await handleInvalidSession();
          return;
        }
        if (_event === 'SIGNED_OUT') {
          setUser(null);
          setOrganization(null);
          setSubscription(null);
          return;
        }
        setUser(session?.user ?? null);
        if (session?.user) {
          // For USER_UPDATED events (e.g. profile metadata changes), skip re-fetching
          // the organization if we already have it cached — avoids a race condition
          // where the re-fetch fails and clears the in-memory org_id, breaking all
          // subsequent data fetches across the app.
          if (_event === 'USER_UPDATED' && organization) {
            return;
          }
          await fetchUserOrganization(session?.user?.id);
        } else {
          setOrganization(null);
          setSubscription(null);
        }
      });
      authSubscription = subscription;
    };

    init();

    return () => {
      authSubscription?.unsubscribe();
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  const fetchUserOrganization = async (userId) => {
    try {
      // Get user profile with organization
      const { data: profile, error: profileError } = await supabase?.from('user_profiles')?.select('organization_id')?.eq('id', userId)?.single();

      if (profileError) throw profileError;

      if (profile?.organization_id) {
        // Cache org_id in the shared in-memory store so services never need
        // to re-query user_profiles (avoids stale data after token refresh)
        setAuthOrgId(profile?.organization_id);

        // Get organization details
        const { data: org, error: orgError } = await supabase?.from('organizations')?.select('*')?.eq('id', profile?.organization_id)?.single();

        if (orgError) throw orgError;
        setOrganization(org);

        // Get subscription details
        const { data: sub, error: subError } = await supabase?.from('subscriptions')?.select('*')?.eq('organization_id', profile?.organization_id)?.single();

        if (subError && subError?.code !== 'PGRST116') {
          throw subError;
        }
        setSubscription(sub);
      }
    } catch (error) {
      console.error('Error fetching organization:', error);
    }
  };

  const signUp = async (email, password, metadata = {}) => {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: metadata
        }
      });
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Sign up error:', error?.message);
          throw error;
        }
      }
      
      return data;
    } catch (error) {
      throw error;
    }
  };

  const signIn = async (email, password) => {
    try {
      // Check for demo credentials
      if ((email === 'demo@aducrm.com' || email === 'demo.admin@adubuild.com') && password === 'demo123') {
        // Enable demo mode
        localStorage.setItem('demo_mode', 'true');
        setIsDemoMode(true);
        // Load demo session data
        const demoUser = { email: email, id: 'demo-user-id' };
        setUser(demoUser);

        // Fetch the real demo organization from Supabase so services can query with a real org_id
        try {
          const { data: demoOrg } = await supabase
            ?.from('organizations')
            ?.select('*')
            ?.eq('is_demo', true)
            ?.limit(1)
            ?.single();
          if (demoOrg?.id) {
            localStorage.setItem('demo_org_id', demoOrg?.id);
            setOrganization(demoOrg);
          }
        } catch (orgErr) {
          console.warn('Could not fetch demo org:', orgErr);
        }

        return { user: { email: email } };
      }

      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Sign in error:', error?.message);
          throw error;
        }
      }
      
      return data;
    } catch (error) {
      throw error;
    }
  };

  const signOut = async () => {
    try {
      // Handle demo mode logout
      if (isDemoMode) {
        localStorage.removeItem('demo_mode');
        localStorage.removeItem('demo_org_id');
        setAuthOrgId(null);
        setIsDemoMode(false);
        setUser(null);
        setOrganization(null);
        setSubscription(null);
        return;
      }

      const { error } = await supabase?.auth?.signOut();
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Sign out error:', error?.message);
        }
      }
      setOrganization(null);
      setSubscription(null);
      setAuthOrgId(null);
    } catch (error) {
      throw error;
    }
  };

  const resetPassword = async (email) => {
    try {
      const { error } = await supabase?.auth?.resetPasswordForEmail(email);
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Password reset error:', error?.message);
          throw error;
        }
      }
    } catch (error) {
      throw error;
    }
  };

  const updateUserMetadata = async (metadata) => {
    try {
      const { data, error } = await supabase?.auth?.updateUser({
        data: metadata
      });
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Update metadata error:', error?.message);
          throw error;
        }
      }
      
      return data;
    } catch (error) {
      throw error;
    }
  };

  const value = {
    user,
    loading,
    organization,
    subscription,
    isDemoMode,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updateUserMetadata,
    refreshOrganization: () => user ? fetchUserOrganization(user?.id) : null
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
